import { For, Show } from 'solid-js'
import type { HubCheckIn } from '../../lib/hub'

/**
 * What the guest is carrying right now — per-item, with real tag numbers.
 *
 * This card is the guest's answer to "what do I have in there?". It had the data
 * but told a very thin story: a venue name and a list of tags, with no money, no
 * fee, no time limit, and no way to tell a coat that is still held from one
 * already handed back. At a counter the guest needs to point at the exact tag AND
 * know what the visit will cost, so all of it is on the card now.
 */
export default function ActiveCheckInCard(props: {
  activeCheckIns: HubCheckIn[]
  now?: Date
}) {
  const now = () => props.now ?? new Date()

  /** "45 min", "3 h", "2 d" — how long the item has been held. */
  const heldFor = (createdAt: string): string => {
    const started = new Date(createdAt).getTime()
    if (Number.isNaN(started)) return ''
    const mins = Math.max(0, Math.floor((now().getTime() - started) / 60000))
    if (mins < 60) return `${mins} min`
    const hours = Math.floor(mins / 60)
    if (hours < 24) return `${hours} h ${mins % 60} min`
    return `${Math.floor(hours / 24)} d`
  }

  const euros = (cents: number | null | undefined) => `€${((cents ?? 0) / 100).toFixed(2)}`

  /** Items the guest still physically holds; anything else was handed back. */
  const held = (checkIn: HubCheckIn) =>
    (checkIn.items ?? []).filter((i) => i.status !== 'checked_out' && i.status !== 'returned')

  return (
    <section class="rounded-2xl bg-emerald-500/10 border border-emerald-400/30 p-5">
      <div class="flex items-center justify-between mb-3">
        <h2 class="text-sm font-semibold text-emerald-200">At the cloakroom now</h2>
        <span class="text-xs text-emerald-300/80">
          {props.activeCheckIns.length} {props.activeCheckIns.length === 1 ? 'session' : 'sessions'}
        </span>
      </div>

      <For each={props.activeCheckIns}>
        {(checkIn) => (
          <div class="mb-4 last:mb-0">
            <div class="flex items-baseline justify-between">
              <p class="font-semibold text-white">{checkIn.venueName}</p>
              <Show when={heldFor(checkIn.createdAt)}>
                <span class="text-xs text-white/50">{heldFor(checkIn.createdAt)}</span>
              </Show>
            </div>

            {/* The tag numbers are the whole point: the guest must be able to
                point at the exact item they handed over. */}
            <ul class="mt-2 space-y-1.5">
              <For each={held(checkIn)}>
                {(item) => (
                  <li class="flex items-center justify-between rounded-lg bg-black/20 px-3 py-2">
                    <span class="text-sm text-white/90 capitalize">{item.type}</span>
                    <span class="font-mono text-sm font-semibold text-emerald-200">#{item.tagNumber}</span>
                  </li>
                )}
              </For>
              <Show when={held(checkIn).length === 0}>
                <li class="rounded-lg bg-black/20 px-3 py-2 text-sm text-white/50">
                  All items returned — this session closes on checkout.
                </li>
              </Show>
            </ul>

            {/* What the visit will cost. Not charged until the last item comes
                back, so "due on collection" is the honest label. */}
            <div class="mt-2 flex items-center justify-between text-xs">
              <span class="text-white/50">Due on collection</span>
              <span class="font-semibold text-white">{euros(checkIn.totalAmount)}</span>
            </div>
          </div>
        )}
      </For>
    </section>
  )
}
